import React, { useEffect, useState } from "react";

import { Turnstile } from "@marsidev/react-turnstile";
import { Link, Navigate, useNavigate } from "react-router-dom";

import { Header } from "../components/layout/header";
import { RedirectProgressBar } from "../components/redirect-progress-bar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { config } from "../config";
import { useAuth } from "../contexts/auth-context";
import { useIsDark } from "../contexts/theme-context";
import { useTranslations } from "../hooks/use-translations";
import { apiClient } from "../lib/api";

export const LoginPage = (): React.JSX.Element => {
  const isDark = useIsDark();
  const [email, setEmail] = useState(``);
  const [password, setPassword] = useState(``);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(``);
  const [turnstileToken, setTurnstileToken] = useState(``);
  const [redirecting, setRedirecting] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState(``);
  const turnstileEnabled = config.features.turnstileProtection;
  const csrfEnabled = config.features.csrfProtection;
  const { turnstileSiteKey } = config;

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const { translations } = useTranslations();

  const oauthProviders = [
    { key: `google`, label: translations.common.providerGoogle },
    { key: `microsoft`, label: translations.common.providerMicrosoft },
    { key: `facebook`, label: translations.common.providerFacebook },
    { key: `github`, label: translations.common.providerGithub },
  ] as const;

  const [enabledProviders, setEnabledProviders] = useState<Record<string, boolean>>({ ...config.oauth });

  const activeProviders = oauthProviders.filter((p) => Boolean(enabledProviders[p.key]));

  useEffect(() => {
    const loadProviders = async () => {
      try {
        const response = await fetch(`${config.apiBaseUrl}/api/auth/providers`, { credentials: `include` });

        const data = (await response.json()) as { providers?: Record<string, boolean> };

        if (data?.providers) {
          setEnabledProviders(data.providers);
        }
      } catch {
        // ignore; keep defaults from config
      }
    };

    loadProviders();
  }, []);

  useEffect(() => {
    if (csrfEnabled) {
      const loadToken = async () => {
        const data = await apiClient.fetchCsrfToken();

        apiClient.setCsrfToken(data.csrfToken);
      };

      loadToken();
    }
  }, [csrfEnabled]);

  if (isAuthenticated) {
    return <Navigate replace to="/account/data" />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(``);

    try {
      const loginResponse = await login(email, password, turnstileToken);

      if (!loginResponse.success) {
        const MessageKey = loginResponse.message;
        const translated = translations.auth[MessageKey] ?? translations.login.failure;

        setError(translated);
        return;
      }

      navigate(`/account/data`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : translations.login.failure;

      if (errorMessage === `accountNotConfirmed`) {
        const errorWithDetails = err as Error & {
          details?: Record<string, unknown>;
        };

        const lastSentAt = errorWithDetails.details?.lastSentAt as string | undefined;
        const retryAfterMinutes = errorWithDetails.details?.retryAfterMinutes as number | undefined;

        const search = new URLSearchParams();

        search.set(`email`, email);

        if (lastSentAt) {
          search.set(`lastSentAt`, lastSentAt);
        }

        if (retryAfterMinutes != null) {
          search.set(`retryAfterMinutes`, String(retryAfterMinutes));
        }

        const targetUrl = `/resend-confirmation-email?${search.toString()}`;

        setRedirectUrl(targetUrl);
        setRedirecting(true);

        return;
      }

      if (translations.auth[errorMessage as keyof typeof translations.auth]) {
        setError(translations.auth[errorMessage as keyof typeof translations.auth]);
      } else if (errorMessage === `unexpectedError` || errorMessage.includes(`Internal server error`)) {
        setError(translations.common.serverError);
      } else if (errorMessage === `networkError` || errorMessage.includes(`Network error`)) {
        setError(translations.common.networkError);
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const wrapperBg = isDark ? `bg-gray-900` : `bg-white`;

  return (
    <div className={`min-h-screen flex flex-col ${wrapperBg}`}>
      <Header />
      <div className="flex flex-1 items-center justify-center px-4 relative">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl text-center" data-testid="page-title">
              {translations.login.title}
            </CardTitle>
            <CardDescription className="text-center">{translations.login.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-4" onSubmit={handleSubmit}>
              {error ? (
                <div
                  className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/50 dark:text-red-400 dark:border-red-800"
                  data-testid="login-error"
                >
                  {error}
                  {error.includes(translations.common.serverError) ? (
                    <div className="mt-2 text-xs">
                      <span>{translations.common.supportContact} </span>
                      <a className="underline hover:no-underline" href={`mailto:${config.supportEmail}`}>
                        {config.supportEmail}
                      </a>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <Input
                required
                autoComplete="email"
                data-testid="email-input"
                id="email"
                label={translations.common.emailLabel}
                name="email"
                onChange={(e) => setEmail(e.target.value)}
                placeholder={translations.common.emailPlaceholder}
                type="email"
                value={email}
              />

              <Input
                required
                autoComplete="current-password"
                data-testid="password-input"
                id="password"
                label={translations.common.passwordLabel}
                name="password"
                onChange={(e) => setPassword(e.target.value)}
                placeholder={translations.common.passwordPlaceholder}
                type="password"
                value={password}
              />

              {turnstileEnabled ? <Turnstile onSuccess={setTurnstileToken} siteKey={turnstileSiteKey} /> : null}

              {redirecting ? (
                <RedirectProgressBar
                  redirectUrl={redirectUrl}
                  title={translations.common.redirectingToResendConfirmationEmail}
                />
              ) : null}

              <Button
                className="w-full"
                data-testid="login-submit"
                disabled={!email || !password || (turnstileEnabled && !turnstileToken)}
                isLoading={isLoading}
                loadingText={translations.common.loading}
                type="submit"
              >
                {translations.common.signIn}
              </Button>
            </form>
            {activeProviders.length > 0 && (
              <div className="mt-4 flex flex-col gap-2">
                {activeProviders.map((provider) => (
                  <Button
                    key={provider.key}
                    className="w-full"
                    onClick={() => {
                      window.location.href = `${config.apiBaseUrl}/api/auth/${provider.key}`;
                    }}
                  >
                    {translations.common.signInWith.replace(`{provider}`, provider.label)}
                  </Button>
                ))}
              </div>
            )}
            <div className="mt-6 text-center space-y-2">
              {config.features.passwordReset ? (
                <div>
                  <Link
                    className="text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400"
                    to="/forgot-password"
                  >
                    {translations.login.forgotPassword}
                  </Link>
                </div>
              ) : null}
              <div>
                <span className="text-sm text-gray-500 dark:text-gray-400">{`${translations.login.noAccount} `}</span>
                <Link
                  className="text-sm font-semibold text-sky-600 hover:text-red-600 dark:text-sky-400 dark:hover:text-red-400"
                  to="/register"
                >
                  {translations.common.signUp}
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <footer className="py-3">
        <div className="text-center text-xs text-gray-500 dark:text-gray-400">
          <span>{translations.common.supportContact} </span>
          <a className="underline hover:no-underline" href={`mailto:${config.supportEmail}`}>
            {config.supportEmail}
          </a>
        </div>
      </footer>
    </div>
  );
};
